// Imports => React
import { useEffect, useRef, useState, useMemo, memo } from 'react';
import ReactCSSTransitionReplace from 'react-css-transition-replace';
import Favico from 'favico.js';
import clsx from 'clsx';

// Imports => Constants
import {
  THEMES,
  TYPES,
} from '@opp/ui-kit';

// Imports => Atoms
import {
  AcContainer,
  AcRow,
  AcColumn,
  AcButton,
  AcRichContent,
} from '@opp/ui-kit';
import {withTranslation} from "react-i18next";

const _CLASSES = {
  MAIN: 'ac-timeout-notice-modal ac-modal--size-sm',
  DENSE: 'ac-timeout-notice-modal--dense',
  CONTENT: 'ac-timeout-notice-modal__content',
};

let ttTimer = null;

const AcTimeoutNoticeModal = ({ callback, update, cancel, t }) => {
  const [remaining, setRemaining] = useState(60);
  const [ttUnAuth, setTTUnAuth] = useState(false);

  const favicon = useRef(null);

  const addEvents = () => {
    window.addEventListener('cancelCountdown', handleCancel, false);
    window.addEventListener('unAuthenticate', handleUnAuthenticate, false);
  };

  const removeEvents = () => {};

  const getSetFaviconElements = (putback = false) => {
    const head = document.querySelector('head');

    if (head) {
      if (!putback) {
        const elems = head.querySelectorAll(
          'link[rel*="icon"][type="image/png"]'
        );
        let res = [];
        elems.forEach((n) => {
          const id = n.getAttribute('id');
          if (!id) res.push(n);
        });
        window.originalElems = res;

        res.forEach((n) => n.remove());
      } else {
        if (window.originalElems)
          window.originalElems.forEach((n) => head.append(n));
      }
    }
  };

  const endSession = () => {
    clearTimeout(ttTimer);

    if (window.favicon && window.favicon.reset) window.favicon.reset();
    getSetFaviconElements(true);
    setTTUnAuth(true);
    if (update) update();
  };

  const handleEndSession = () => {
    endSession();
    const unauthenticatedEvent = new CustomEvent('unAuthenticate');
    window.dispatchEvent(unauthenticatedEvent);
  };

  const handleCancel = (event) => {
    if (event && event.preventDefault) event.preventDefault();
    clearTimeout(ttTimer);

    if (window.favicon && window.favicon.reset) window.favicon.reset();
    getSetFaviconElements(true);
    setTTUnAuth(false);

    if (callback) callback();
  };

  const handleUnAuthenticate = (event) => {
    endSession();
  };

  const handleReload = (event) => {
    if (event && event.preventDefault) event.preventDefault();
    clearTimeout(ttTimer);

    if (window && window.location) window.location.reload();
  };

  useEffect(() => {
    getSetFaviconElements();
    favicon.current = new Favico({
      animation: 'none',
      type: 'circle',
      position: 'center',
      fontFamily: `'hero-new', arial, sans-serif`,
    });
    window.favicon = favicon.current;
  }, []);

  useEffect(() => {
    addEvents();

    if (!ttUnAuth) {
      ttTimer = setTimeout(() => {
        if (remaining <= 0) {
          clearTimeout(ttTimer);
          handleEndSession();
        } else {
          const next = remaining - 1;

          if (window.favicon && window.favicon.badge)
            window.favicon.badge(next);
          setRemaining(next);
        }
      }, 1000);
    }
  }, [ttUnAuth, remaining]);

  const renderInactiveMessage = useMemo(() => {
    const unit = t('timeout.second', {count: remaining, ns: 'common'});
    let message = `<p>${t('timeout.timeout-line-1', 'You have been inactive for a while.', {ns: 'common'})}</p>`;
    message += `<p class="h-margin-top-15">${t('timeout.timeout-line-2', 'For your and our security, we will automatically log you out in', {ns: 'common'})}</p>`;
    message += `<p class="h-margin-y-15"><strong rel="${remaining}"><span>${remaining}</span> ${unit}</strong>.</p>`;
    message += `<p>${t('timeout.timeout-line-3', 'Do you want to stay logged in and keep going?', {ns: 'common'})}</p>`;

    return (
      <AcRichContent
        className={'ac-timeout-notice-message'}
        content={message}
      />
    );
  }, [remaining, ttUnAuth]);

  const renderSignedOutMessage = useMemo(() => {
    let message = `<p>${t('timeout.timed-out-line-1', 'Due to browser inactivity you have been logged out and your session has ended.', {ns: 'common'})}</p>`;
    message += `<p class="h-margin-top-15">${t('timeout.timed-out-line-2', 'Log in again if you wish to get back in.', {ns: 'common'})}</p>`;

    return <AcRichContent content={message} />;
  }, [ttUnAuth]);

  const getContentClassNames = useMemo(() => {
    return clsx([_CLASSES.CONTENT]);
  }, []);

  const getStyleClassNames = useMemo(() => {
    return clsx([_CLASSES.MAIN, ttUnAuth && _CLASSES.DENSE]);
  }, [ttUnAuth]);

  const getSubmitButtonOptions = useMemo(() => {
    const theme = ttUnAuth ? THEMES.OMEGA : THEMES.OMEGA;
    const title = ttUnAuth ? t('timeout.log-in', 'Log in', {ns: 'common'}) : t('timeout.keep-logged-in', 'Keep me logged in', {ns: 'common'});
    const handleCallback = ttUnAuth ? handleReload : handleCancel;

    return {
      className: 'h-margin-bottom-20',
      type: TYPES.BUTTON,
      theme,
      title,
      callback: handleCallback,
    };
  }, [ttUnAuth, handleCancel, handleReload]);

  return (
    <div className={getStyleClassNames}>
      <div className={getContentClassNames}>
        <ReactCSSTransitionReplace
          transitionName="fade-wait"
          transitionEnterTimeout={500}
          transitionLeaveTimeout={500}
        >
          <div key={ttUnAuth}>
            {!ttUnAuth && (
              <AcContainer fluid>
                <AcRow>
                  <AcColumn className={'h-text--align-center h-margin-top-10'}>
                    {renderInactiveMessage}
                  </AcColumn>
                </AcRow>

                <AcRow className={'h-margin-top-40'}>
                  <AcColumn className={'h-text--align-center'}>
                    <AcButton {...getSubmitButtonOptions}>
                      <span>{t('timeout.keep-logged-in', 'Keep me logged in', {ns: 'common'})}</span>
                    </AcButton>
                  </AcColumn>
                </AcRow>
              </AcContainer>
            )}
            {ttUnAuth && (
              <AcContainer fluid>
                <AcRow>
                  <AcColumn className={'h-text--align-center h-margin-top-20'}>
                    {renderSignedOutMessage}
                  </AcColumn>
                </AcRow>

                <AcRow className={'h-margin-top-40'}>
                  <AcColumn className={'h-text--align-center'}>
                    <AcButton {...getSubmitButtonOptions}>
                      <span>{t('timeout.log-in', 'Log in', {ns: 'common'})}</span>
                    </AcButton>
                  </AcColumn>
                </AcRow>
              </AcContainer>
            )}
          </div>
        </ReactCSSTransitionReplace>
      </div>
    </div>
  );
};

export default withTranslation(['common'])(memo(AcTimeoutNoticeModal));
